// cuotasUtil.js — helpers PUROS y entity-agnostic para planes de cuotas.
// No dependen de React, Supabase ni de ninguna entidad (factura/activo/edificio).
// El modal de cuotas de activos (Fase 1 · commit 2) los consumirá. La lógica replica el
// `generarCuotas` de PlanCuotasModal (montos iguales, la última absorbe el residuo) + su
// validación de suma; las FACTURAS se migran a este util en una limpieza futura, NO ahora.

// Pasos de frecuencia soportados. Mensual es el default (mismo comportamiento que el modal).
const PERIODOS = {
  semanal: { dias: 7 },
  quincenal: { dias: 15 },
  mensual: { meses: 1 },
  bimestral: { meses: 2 },
  trimestral: { meses: 3 },
  anual: { meses: 12 },
};

// Parse 'YYYY-MM-DD' | Date → [y, m, d] en horario LOCAL (evita el corrimiento de zona que
// produce `new Date('YYYY-MM-DD')`, que interpreta el string como UTC).
const parseFechaLocal = (fecha) => {
  if (fecha instanceof Date) return [fecha.getFullYear(), fecha.getMonth() + 1, fecha.getDate()];
  const soloFecha = String(fecha).includes("T") ? String(fecha).substring(0, 10) : String(fecha);
  const [y, m, d] = soloFecha.split("-").map(Number);
  return [y, m, d];
};

const pad2 = (n) => String(n).padStart(2, "0");

// Suma `k` períodos de `frecuencia` a la fecha base y devuelve 'YYYY-MM-DD'. Para saltos por meses
// clampea el día al último día del mes destino (31-ene + 1 mes → 28/29-feb, no 3-mar).
const sumarPeriodos = (fechaBase, frecuencia, k) => {
  const [y, m, d] = parseFechaLocal(fechaBase);
  const paso = PERIODOS[frecuencia] || PERIODOS.mensual;
  if (paso.dias) {
    const dt = new Date(y, m - 1, d + paso.dias * k);
    return `${dt.getFullYear()}-${pad2(dt.getMonth() + 1)}-${pad2(dt.getDate())}`;
  }
  const totalMes = m - 1 + paso.meses * k;
  const anio = y + Math.floor(totalMes / 12);
  const mes = (totalMes % 12) + 1; // 1..12
  const ultimoDia = new Date(anio, mes, 0).getDate(); // día 0 del mes siguiente = último del mes
  return `${anio}-${pad2(mes)}-${pad2(Math.min(d, ultimoDia))}`;
};

// Genera N cuotas de montos iguales (la última absorbe el residuo del redondeo), con vencimientos
// a +1..+N períodos de `frecuencia` desde `fechaInicio` (la 1ª cuota vence un período después,
// igual que el modal de facturas). Devuelve [{ numero_cuota, monto_cuota, fecha_vencimiento }].
// Los montos y fechas quedan editables por el consumidor; esto es solo el arranque.
export const generarCuotas = (total, n, fechaInicio, frecuencia = "mensual") => {
  const numCuotas = Math.trunc(Number(n));
  if (!Number.isFinite(numCuotas) || numCuotas < 1) return [];
  const totalNum = Number(total) || 0;
  const montoPorCuota = Math.floor(totalNum / numCuotas);
  const residuo = totalNum - montoPorCuota * numCuotas;

  return Array.from({ length: numCuotas }, (_, i) => ({
    numero_cuota: i + 1,
    monto_cuota: i === numCuotas - 1 ? montoPorCuota + residuo : montoPorCuota,
    fecha_vencimiento: sumarPeriodos(fechaInicio, frecuencia, i + 1),
  }));
};

// True si la suma de las cuotas cuadra con el total (tolerancia de 1 unidad por redondeo).
// Mismo criterio que PlanCuotasModal: |Σ monto_cuota − total| ≤ 1.
export const validarSumaCuotas = (cuotas, total) => {
  const suma = (cuotas || []).reduce((s, c) => s + Number(c.monto_cuota || 0), 0);
  return Math.abs(suma - (Number(total) || 0)) <= 1;
};
